import React, { createContext, useContext, useState, useEffect } from 'react';
import { useCustomerFieldConfigs, type CustomerFieldConfig } from '@/hooks/useCustomerFieldConfigs';
import type { Customer } from '@/hooks/useCustomers';
import { ValidationErrorBoundary } from '@/components/ValidationErrorBoundary';
import { ValidationPerformanceMonitor } from '@/components/ValidationPerformanceMonitor';
import { ValidationSecurityChecker } from '@/components/ValidationSecurityChecker';

interface ValidationResult {
  valid: boolean;
  errors: string[];
}

interface ValidationState {
  [fieldName: string]: ValidationResult;
}

interface CustomerValidationContextValue {
  selectedCustomer: Customer | null;
  configs: CustomerFieldConfig[];
  mandatoryConfigs: CustomerFieldConfig[];
  configsByGroup: Record<string, CustomerFieldConfig[]>;
  validationState: ValidationState;
  completedMandatory: number;
  totalMandatory: number;
  completionPercentage: number;
  allFieldsValid: boolean;
  canComplete: boolean;
  setSelectedCustomer: (customer: Customer | null) => void;
  validateField: (fieldName: string, value: any) => ValidationResult;
  updateFieldValue: (fieldName: string, value: any) => void;
  validateAllFields: (data: Record<string, any>) => void;
  getFieldConfig: (fieldName: string) => CustomerFieldConfig | undefined;
  isLoading: boolean;
}

const CustomerValidationContext = createContext<CustomerValidationContextValue | null>(null);

export const useCustomerValidation = () => {
  const context = useContext(CustomerValidationContext);
  if (!context) {
    throw new Error('useCustomerValidation must be used within CustomerValidationProvider');
  }
  return context;
};

interface CustomerValidationProviderProps {
  children: React.ReactNode;
  initialCustomer?: Customer | null;
  onValidationChange?: (isValid: boolean, completionPercentage: number) => void;
}

export const CustomerValidationProvider = ({ 
  children, 
  initialCustomer = null,
  onValidationChange 
}: CustomerValidationProviderProps) => {
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(initialCustomer);
  const [validationState, setValidationState] = useState<ValidationState>({});
  const [fieldValues, setFieldValues] = useState<Record<string, any>>({});

  const { 
    configs, 
    mandatoryConfigs, 
    configsByGroup,
    validateField: validateSingleField,
    validateAllFields: validateAllFieldsInternal,
    getFieldConfig,
    isLoading 
  } = useCustomerFieldConfigs(selectedCustomer?.id);

  const validateField = (fieldName: string, value: any): ValidationResult => {
    return validateSingleField(fieldName, value);
  };

  const updateFieldValue = (fieldName: string, value: any) => {
    setFieldValues(prev => ({ ...prev, [fieldName]: value }));
    
    // Validate the field immediately
    const result = validateField(fieldName, value);
    setValidationState(prev => ({
      ...prev,
      [fieldName]: result
    }));
  };

  const validateAllFields = (data: Record<string, any>) => {
    const results = validateAllFieldsInternal(data);
    setValidationState(results.validationResults);
    setFieldValues(data);
  };

  // Calculate validation metrics
  const completedMandatory = mandatoryConfigs.filter(config => {
    const value = fieldValues[config.field_name];
    const isValid = validationState[config.field_name]?.valid !== false;
    return value && value !== '' && isValid;
  }).length;

  const totalMandatory = mandatoryConfigs.length;
  const completionPercentage = totalMandatory > 0 ? (completedMandatory / totalMandatory) * 100 : 100;

  const allFieldsValid = Object.values(validationState).every(result => result.valid);
  const canComplete = selectedCustomer && completedMandatory === totalMandatory && allFieldsValid;

  // Notify parent of validation changes
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(Boolean(canComplete), completionPercentage);
    }
  }, [canComplete, completionPercentage, onValidationChange]);

  // Reset validation state when customer changes
  useEffect(() => {
    setValidationState({});
    setFieldValues({});
  }, [selectedCustomer?.id]);

  const value: CustomerValidationContextValue = {
    selectedCustomer,
    configs,
    mandatoryConfigs,
    configsByGroup,
    validationState,
    completedMandatory,
    totalMandatory,
    completionPercentage,
    allFieldsValid,
    canComplete: Boolean(canComplete),
    setSelectedCustomer,
    validateField,
    updateFieldValue,
    validateAllFields,
    getFieldConfig,
    isLoading
  };

  return (
    <ValidationErrorBoundary
      onError={(error, errorInfo) => {
        console.error('Customer validation error:', error, errorInfo);
      }}
    >
      <CustomerValidationContext.Provider value={value}>
        {children}
        {process.env.NODE_ENV === 'development' && (
          <>
            <ValidationPerformanceMonitor 
              onPerformanceIssue={(issue) => {
                console.warn('Validation performance issue:', issue);
              }}
            />
            <ValidationSecurityChecker 
              data={fieldValues}
              onSecurityIssue={(issues) => {
                console.warn('Security validation issues:', issues);
              }}
            />
          </>
        )}
      </CustomerValidationContext.Provider>
    </ValidationErrorBoundary>
  );
};